import React, { useState } from 'react';
import { Form, Input, Button, App, Space, Checkbox } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import emailService from '../../services/emailService';

const { TextArea } = Input;

const EmailCompose = ({ leadId, leadEmail, leadName, onEmailSent }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [showCC, setShowCC] = useState(false);
  const [showBCC, setShowBCC] = useState(false);

  const handleSend = async (values) => {
    setSending(true);
    try {
      const emailData = {
        leadId,
        to: values.to,
        cc: values.cc ? values.cc.split(',').map(e => e.trim()).filter(e => e) : [],
        bcc: values.bcc ? values.bcc.split(',').map(e => e.trim()).filter(e => e) : [],
        subject: values.subject,
        body: values.body
      };

      const result = await emailService.sendEmail(emailData);
      
      if (result.success) {
        message.success('Email sent successfully!');
        form.resetFields();
        setShowCC(false);
        setShowBCC(false);
        if (onEmailSent) {
          onEmailSent(result.data);
        }
      } else {
        message.error(result.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to send email';
      message.error(errorMsg);
      
      // Log more details for debugging
      if (error.response?.data?.details) {
        console.error('Error details:', error.response.data.details);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="email-compose">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSend}
        initialValues={{
          to: leadEmail || '',
          subject: `Follow up with ${leadName || 'Lead'}`
        }}
      >
        <Form.Item
          label="To"
          name="to"
          rules={[
            { required: true, message: 'Please enter recipient email' },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
        >
          <Input placeholder="recipient@example.com" />
        </Form.Item>

        <Space style={{ marginBottom: 16 }}>
          <Checkbox 
            checked={showCC} 
            onChange={(e) => {
              setShowCC(e.target.checked);
              if (!e.target.checked) {
                form.setFieldValue('cc', undefined);
              }
            }}
          >
            Add CC
          </Checkbox>
          <Checkbox 
            checked={showBCC} 
            onChange={(e) => {
              setShowBCC(e.target.checked);
              if (!e.target.checked) {
                form.setFieldValue('bcc', undefined);
              }
            }}
          >
            Add BCC
          </Checkbox>
        </Space>

        {showCC && (
          <Form.Item
            label="CC (comma separated)"
            name="cc"
          >
            <Input placeholder="cc1@example.com, cc2@example.com" />
          </Form.Item>
        )}

        {showBCC && (
          <Form.Item
            label="BCC (comma separated)"
            name="bcc"
          >
            <Input placeholder="bcc1@example.com, bcc2@example.com" />
          </Form.Item>
        )}

        <Form.Item
          label="Subject"
          name="subject"
          rules={[{ required: true, message: 'Please enter email subject' }]}
        >
          <Input placeholder="Email subject" />
        </Form.Item>

        <Form.Item
          label="Message"
          name="body"
          rules={[{ required: true, message: 'Please enter email message' }]}
        >
          <TextArea
            rows={12}
            placeholder="Type your message here..."
            style={{ fontFamily: 'inherit' }}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              loading={sending}
              size="large"
            >
              Send Email
            </Button>
            <Button
              onClick={() => form.resetFields()}
              disabled={sending}
            >
              Clear
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default EmailCompose;
